// Public docs for the WAXCreator Minting API (/api/v1) and MCP server (/mcp).
// Keep in sync with netlify/lib/spec.ts — that file is the source of truth;
// the live OpenAPI document at /api/v1/openapi.json is generated from it.
const origin = typeof window !== "undefined" ? window.location.origin : "https://waxcreator.com";

const pre: React.CSSProperties = {
  background: "rgba(0,0,0,0.35)",
  padding: "12px 14px",
  borderRadius: 8,
  overflowX: "auto",
  fontSize: 13,
  lineHeight: 1.5,
  whiteSpace: "pre",
};

export function Developers() {
  return (
    <div className="page">
      <div className="page__header">
        <h1>Developers &amp; AI agents</h1>
        <p className="page__subtitle">
          Mint WAX NFTs from your own code or AI agent through a free, open API — no API key, no sign-up. It never
          touches private keys: every write returns an unsigned transaction that your own WAX account signs.
        </p>
      </div>

      <nav className="tutorial-toc" aria-label="Sections">
        <div className="tutorial-toc__group">
          <span className="tutorial-toc__label">Start</span>
          <a href="#how">How it works</a>
          <a href="#quickstart">Quickstart</a>
        </div>
        <div className="tutorial-toc__group">
          <span className="tutorial-toc__label">Reference</span>
          <a href="#endpoints">Endpoints</a>
          <a href="#mcp">MCP for agents</a>
          <a href="#agent-keys">Agent key safety</a>
        </div>
        <div className="tutorial-toc__group">
          <span className="tutorial-toc__label">Machine-readable</span>
          <a href="/api/v1/openapi.json">openapi.json</a>
          <a href="/llms.txt">llms.txt</a>
        </div>
      </nav>

      <section className="tutorial-section" id="how">
        <h2>How it works</h2>
        <ol className="tutorial-steps">
          <li>
            <strong>Prepare.</strong> Call a <code>prepare</code> endpoint with who's minting and what. The API checks
            the chain, adds any one-time setup (collection, <code>waxcreator</code> schema, escrow wiring) and returns
            the unsigned transaction.
          </li>
          <li>
            <strong>Sign.</strong> Either hand <code>actions</code> to a wallet SDK (WharfKit, eosjs, Anchor), or sign{" "}
            <code>signing_digest</code> with the account's key.
          </li>
          <li>
            <strong>Push.</strong> Wallet SDKs broadcast for you. Raw signers POST{" "}
            <code>{"{ serialized_transaction, signatures }"}</code> to <code>/api/v1/transactions/push</code>. The
            response includes the new <code>minted_asset_ids</code> / <code>template_ids</code>.
          </li>
        </ol>
        <p className="tutorial-note">
          Because the creator's own signature authorizes every mint, nobody — including WAXCreator — can mint in
          your collection or spend your WAX through this API.
        </p>
      </section>

      <section className="tutorial-section" id="quickstart">
        <h2>Quickstart</h2>
        <p>1. Pin your image (skip if you already have an IPFS CID):</p>
        <pre style={pre}>{`curl -X POST ${origin}/api/v1/images \\
  -H 'Content-Type: application/json' \\
  -d '{"url":"https://example.com/art.png"}'
# → { "cid": "Qm...", ... }`}</pre>
        <p>2. Prepare a one-of-one mint:</p>
        <pre style={pre}>{`curl -X POST ${origin}/api/v1/mint/prepare \\
  -H 'Content-Type: application/json' \\
  -d '{"creator":"youraccount","name":"My NFT","image":"Qm...",
       "description":"Made by my agent","attributes":{"rarity":"rare","power":7}}'`}</pre>
        <p>3a. Sign &amp; broadcast with WharfKit (browser/Node):</p>
        <pre style={pre}>{`const prep = await (await fetch("${origin}/api/v1/mint/prepare", {...})).json();
await session.transact({ actions: prep.actions });`}</pre>
        <p>3b. …or sign the digest with a key your agent controls and push:</p>
        <pre style={pre}>{`import { PrivateKey, Checksum256 } from "@wharfkit/antelope";
const key = PrivateKey.from(process.env.WAX_MINT_KEY);   // a limited "mint" permission key
const sig = key.signDigest(Checksum256.from(prep.signing_digest)).toString();
await fetch("${origin}/api/v1/transactions/push", {
  method: "POST", headers: { "Content-Type": "application/json" },
  body: JSON.stringify({ serialized_transaction: prep.serialized_transaction, signatures: [sig] }),
});`}</pre>
      </section>

      <section className="tutorial-section" id="endpoints">
        <h2>Endpoints</h2>
        <p className="tutorial-section__intro">
          Base URL <code>{origin}/api/v1</code>. JSON in, JSON out, CORS open. Full schemas in{" "}
          <a href="/api/v1/openapi.json">openapi.json</a>.
        </p>
        <ul className="tutorial-list">
          <li><code>GET /config</code> — network, chain id, contract accounts</li>
          <li><code>GET /accounts/{"{account}"}/setup</code> — collection/schema/escrow status for a creator</li>
          <li><code>POST /mint/prepare</code> — one-of-one NFT (auto-creates collection + schema)</li>
          <li><code>POST /templates/prepare</code> — reusable template for editions, drops, or backing (<code>backed: true</code>)</li>
          <li><code>POST /mint/template/prepare</code> — mint 1–40 copies of a template</li>
          <li><code>POST /mint/backed/prepare</code> — deposit WAX to mint a backed copy (registers the rate if new)</li>
          <li><code>GET /drops/{"{id}"}</code> · <code>POST /drops/{"{id}"}/mint/prepare</code> — join a live drop</li>
          <li><code>POST /images</code> — pin an image to IPFS (20/hour per IP)</li>
          <li><code>POST /transactions/push</code> — broadcast a signed transaction</li>
        </ul>
        <p className="tutorial-note">
          Rate limits per IP: 60 prepares/min, 30 pushes/min, 20 image uploads/hour. Unsigned transactions expire
          after 5 minutes by default (<code>expire_seconds</code>, max 3600).
        </p>
      </section>

      <section className="tutorial-section" id="mcp">
        <h2>MCP server for AI agents</h2>
        <p>
          Any agent that speaks the Model Context Protocol can connect to <code>{origin}/mcp</code> (Streamable
          HTTP, no auth). It exposes the same operations as tools: <code>waxcreator_prepare_mint</code>,{" "}
          <code>waxcreator_upload_image</code>, <code>waxcreator_push_transaction</code>, and more.
        </p>
        <p>In Claude: Settings → Connectors → Add custom connector → paste the URL. For config-file clients:</p>
        <pre style={pre}>{`{
  "mcpServers": {
    "waxcreator": { "type": "http", "url": "${origin}/mcp" }
  }
}`}</pre>
      </section>

      <section className="tutorial-section" id="agent-keys">
        <h2>Agent key safety</h2>
        <ul className="tutorial-list">
          <li>
            Never give an agent your <code>owner</code> or <code>active</code> key. Create a custom permission (e.g.{" "}
            <code>mint</code>) and <code>linkauth</code> it to only <code>atomicassets::mintasset</code> (and{" "}
            <code>createtempl</code> if needed); pass <code>"permission": "mint"</code> to prepare calls.
          </li>
          <li>
            Backed mints move real WAX into escrow. Keep that on a human-approved wallet, or give the agent's
            permission no access to <code>eosio.token::transfer</code>.
          </li>
          <li>
            Always show the <code>summary</code> field to a human before signing anything that spends funds.
          </li>
        </ul>
        <p className="tutorial-warning">
          Token backing is a fixed 1:1 redemption (burn the NFT, get the exact deposit back), not an investment or
          yield. Smart contracts carry risk; the escrow contract has not yet been independently audited.
        </p>
      </section>
    </div>
  );
}
